"use client";

import { Droplets, Sun, Waves, Wrench, Zap } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
export default function TechnologyImplementationFaq() {
  const careIcons = [{
    icon: Zap,
    label: "DATA"
  }, {
    icon: Wrench,
    label: "TECH"
  }, {
    icon: Waves,
    label: "AUDIT"
  }, {
    icon: Droplets,
    label: "SYNC"
  }, {
    icon: Sun,
    label: "GROW"
  }];
  const rituals = [{
    question: "How do I choose a PMS?",
    body: "Select a system that integrates with your current booking engine and accounting software. Prioritize cloud-based platforms that offer real-time inventory updates and robust reporting features for revenue managers.",
    sub: ["Check API integration depth", "Verify real-time sync speed", "Ensure mobile-first interface"]
  }, {
    question: "What if my data is siloed?",
    body: "Consolidate your data streams into a single source of truth. Use middleware or native integrations to bridge the gap between your OTA channels, website bookings, and property management system.",
    sub: ["Map all data touchpoints", "Automate daily report flows", "Clean historical guest data"]
  }, {
    question: "How often to audit tech?",
    body: "Perform a full technical audit every quarter. Review your channel manager settings, rate parity across OTAs, and ensure your booking engine conversion tracking is firing correctly on all devices.",
    sub: ["Quarterly parity checks", "Review OTA commission rates", "Test booking funnel speed"]
  }, {
    question: "When is a system outdated?",
    body: "If your current stack requires manual entry for inventory or lacks automated yield management, it is costing you revenue. Modern systems should handle dynamic pricing adjustments without human intervention.",
    sub: ["Manual entry is a red flag", "Lack of dynamic yield tools", "Slow load times on mobile"]
  }, {
    question: "How to secure guest data?",
    body: "Implement PCI-compliant payment gateways and ensure all booking traffic is encrypted via SSL. Regularly update your software to patch vulnerabilities and restrict staff access to sensitive guest information.",
    sub: ["Use PCI-compliant gateways", "Enforce strict access roles", "Enable two-factor auth"]
  }, {
    question: "Which tools boost direct?",
    body: "Deploy a high-converting booking engine with integrated upsell modules. Use retargeting pixels and personalized email automation to capture guests who abandoned their booking process.",
    sub: ["Integrated upsell modules", "Abandoned cart email flows", "Personalized guest offers"]
  }, {
    question: "What about OTA connectivity?",
    body: "Ensure your channel manager supports two-way XML connectivity. This prevents overbookings and ensures that rate changes made in your central system reflect instantly across all connected travel platforms.",
    sub: ["Two-way XML sync required", "Instant inventory updates", "Automated rate distribution"]
  }, {
    question: "How to scale tech stack?",
    body: "Start with a solid PMS foundation, then layer on revenue management systems (RMS) and CRM tools. Scale your stack as your property complexity grows to maintain operational efficiency.",
    sub: ["Build on a stable PMS", "Add RMS for yield growth", "Integrate CRM for loyalty"]
  }];
  return <section data-section-id="1265" id="technologyimplementationfaq" className="bg-background text-foreground py-28 md:py-40">
      <div className="max-w-[1320px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div className="flex lg:flex-col lg:items-start gap-4 lg:gap-3 mb-10">
              <div className="flex lg:flex-col items-center lg:items-start gap-3 lg:gap-4 lg:h-32">
                <span className="font-mono text-xs uppercase tracking-[0.22em] text-muted-foreground lg:[writing-mode:vertical-rl] lg:rotate-180">
                  TECH
                </span>
                <span className="font-mono text-xs uppercase tracking-[0.22em] text-muted-foreground lg:[writing-mode:vertical-rl] lg:rotate-180">
                  06 — TECH & IMPLEMENTATION
                </span>
              </div>
            </div>

            <h2 className="font-default font-extralight text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight mb-8 text-foreground">
              Optimize your{" "}
              <span className="font-serif italic font-light text-primary">stack</span>,
              <br />
              maximize your yield.
            </h2>

            <p className="font-default font-light text-base md:text-lg leading-[1.65] text-muted-foreground max-w-md mb-12">
              Modern hospitality requires a seamless tech ecosystem. We help you select, implement, and audit the tools that drive revenue and simplify your daily operations.
            </p>

            {}
            <div className="flex items-center gap-4 mb-10">
              <div className="h-px bg-border flex-1" />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                REV.BA.BA
              </span>
              <div className="w-1.5 h-1.5 rounded-md bg-primary" />
            </div>

            {}
            <div className="grid grid-cols-5 gap-3 md:gap-5">
              {careIcons.map((item, i) => {
              const Icon = item.icon;
              return <div key={i} data-index={i} className="flex flex-col items-start gap-3">
                    <Icon className="w-7 h-7 md:w-8 md:h-8 text-primary" strokeWidth={1.25} />
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {item.label}
                    </span>
                  </div>;
            })}
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full">
              {rituals.map((ritual, i) => {
              const num = String(i + 1).padStart(2, "0");
              return <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b-0 border-t border-border first:border-t group/item data-[state=open]:border-border">
                    <AccordionTrigger className="py-8 hover:no-underline [&>svg]:hidden group">
                      <div className="flex items-start gap-6 md:gap-10 w-full pr-4">
                        <div className="flex items-center gap-2 shrink-0 pt-1">
                          <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground group-data-[state=open]:text-primary transition-colors duration-300">
                            {num} / 08
                          </span>
                          <div className="w-1.5 h-1.5 rounded-md bg-primary opacity-0 group-data-[state=open]:opacity-100 transition-opacity duration-300" />
                        </div>
                        <h4 className="font-default font-normal text-lg md:text-xl leading-[1.3] text-foreground flex-1 text-left">
                          {ritual.question}
                        </h4>
                        <div className="relative w-4 h-4 shrink-0 mt-2">
                          <span className="absolute left-0 top-1/2 w-4 h-px bg-foreground -translate-y-1/2" />
                          <span className="absolute left-1/2 top-0 h-4 w-px bg-foreground -translate-x-1/2 transition-transform duration-300 group-data-[state=open]:rotate-90 group-data-[state=open]:opacity-0" />
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-8 pt-0">
                      <div className="pl-0 md:pl-[7.5rem] pr-10 max-w-2xl">
                        <p className="font-default font-light text-base leading-[1.65] text-muted-foreground mb-6">
                          {ritual.body}
                        </p>
                        <ul className="space-y-2.5">
                          {ritual.sub.map((s, j) => <li key={j} data-index={j} className="flex items-start gap-3 font-default font-light text-sm leading-[1.6] text-muted-foreground">
                              <span className="w-1.5 h-1.5 rounded-md bg-primary mt-2 shrink-0" />
                              <span>{s}</span>
                            </li>)}
                        </ul>
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}